/**
 * Cookie names aligned with @mobile-reality/gatsby-plugin-gdpr-cookies googleConsent.cookieNames.
 * Clarity uses a separate first-party cookie for GTM tag gating.
 */

export const COOKIE = {
  GTM_LOAD: "gatsby-gdpr-google-tagmanager",
  ANALYTICS: "gatsby-gdpr-google-analytics-storage",
  AD_STORAGE: "gatsby-gdpr-google-ad-storage",
  AD_USER_DATA: "gatsby-gdpr-google-ad-user-data",
  AD_PERSONALIZATION: "gatsby-gdpr-google-ad-personalization",
  FUNCTIONALITY: "gatsby-gdpr-google-functionality-storage",
  PERSONALIZATION: "gatsby-gdpr-google-personalization-storage",
  CLARITY: "gatsby-gdpr-clarity",
  GETQUANTY: "gatsby-gdpr-getquanty",
} as const;

/** Google Consent Mode v2 tag keys (snake_case) matching the GDPR plugin. */
export const GOOGLE_CONSENT_TAGS = [
  "ad_storage",
  "analytics_storage",
  "functionality_storage",
  "personalization_storage",
  "ad_user_data",
  "ad_personalization",
] as const;

const tagToCookieName: Record<(typeof GOOGLE_CONSENT_TAGS)[number], keyof typeof COOKIE> = {
  ad_storage: "AD_STORAGE",
  analytics_storage: "ANALYTICS",
  functionality_storage: "FUNCTIONALITY",
  personalization_storage: "PERSONALIZATION",
  ad_user_data: "AD_USER_DATA",
  ad_personalization: "AD_PERSONALIZATION",
};

export function getCookieValue(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
}

export function setCookie(name: string, value: string, days: number): void {
  if (typeof document === "undefined") return;
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value}; expires=${date.toUTCString()}; path=/`;
}

/** True if the GTM container should load (option A: at least one GTM-backed service accepted). */
export function shouldLoadGoogleTagManager(
  analytics: boolean,
  googleAds: boolean,
  clarity: boolean
): boolean {
  return analytics || googleAds || clarity;
}

/** Build gtag consent update payload from current document cookies (granted/denied). */
export function getGoogleConsentPayloadFromCookies(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const tag of GOOGLE_CONSENT_TAGS) {
    const key = tagToCookieName[tag];
    const c = COOKIE[key];
    out[tag] = getCookieValue(c) === "true" ? "granted" : "denied";
  }
  out.security_storage = "granted";
  return out;
}

/** Push consent update to gtag when it already exists (after preference change). */
export function pushGoogleConsentUpdate(): void {
  if (typeof window === "undefined") return;
  const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
  if (typeof gtag !== "function") return;
  gtag("consent", "update", getGoogleConsentPayloadFromCookies());
}
